import { useEffect, useMemo, useState } from "react";

import { buildImplementationGantt } from "@/application/use-cases/board/build-implementation-gantt";
import {
  useBoardFeatures,
  useMilestones,
  useWorkstreams,
} from "@/application/use-cases/board/use-board.queries";

import type { ImplementationGanttViewModel } from "@/application/use-cases/board/build-implementation-gantt";

interface TimelinePageState {
  model: ImplementationGanttViewModel | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Today's date, refreshed when the clock crosses midnight. The gantt's "we are
 * here" line + milestone "reached" state depend on the current day; without this
 * a tab left open overnight would keep showing yesterday until data refetched.
 */
function useToday(): Date {
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    const now = new Date();
    // buildImplementationGantt normalizes `today` to a UTC date, so flip days at
    // the next UTC midnight (not local) to keep the hook + projection in sync.
    const nextUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
    const id = globalThis.setTimeout(() => setToday(new Date()), nextUtcMidnight - now.getTime());
    return () => globalThis.clearTimeout(id);
  }, [today]);
  return today;
}

/** Owns all logic for the live implementation-gantt page. */
export function useTimelinePage(): TimelinePageState {
  const workstreamsQ = useWorkstreams();
  const milestonesQ = useMilestones();
  const featuresQ = useBoardFeatures();

  const isLoading = workstreamsQ.isLoading || milestonesQ.isLoading || featuresQ.isLoading;
  const isError = workstreamsQ.isError || milestonesQ.isError || featuresQ.isError;

  const workstreams = workstreamsQ.data;
  const milestones = milestonesQ.data;
  const features = featuresQ.data;
  const today = useToday();

  const model = useMemo(() => {
    if (!workstreams || !milestones || !features) return null;
    return buildImplementationGantt(workstreams, milestones, features, today);
  }, [workstreams, milestones, features, today]);

  return { model, isLoading, isError };
}
